import type { UserType } from './roles';
import {
  ACADEMY_TRACKS,
  AI_TOOLS,
  AVAILABILITY,
  BUDGET_BANDS,
  COMPANY_NEEDS,
  COMPANY_SIZES,
  EXPERIENCE_BANDS,
  INDUSTRIES,
  LANGUAGES,
  MAKE_INTERESTS,
  PRODUCTION_SKILLS,
  PROJECT_TYPES,
  SKILL_LEVELS,
  SPECIALIZATIONS,
  STUDY_YEARS,
  TIME_COMMITMENT
} from './taxonomy';

export type FieldKind = 'text' | 'textarea' | 'tel' | 'url' | 'single' | 'multi';

export interface Field {
  key: string;
  label: string;
  kind: FieldKind;
  /** Sits under the label. Say why the answer is worth giving, not what a text box is. */
  hint?: string;
  placeholder?: string;
  options?: readonly string[];
  /** `multi` only: the cap, so nobody selects all thirteen crafts. */
  max?: number;
  /** What answering is worth. Weights the completion meter as well as the XP. */
  xp: number;
  optional?: boolean;
}

export interface Badge {
  id: string;
  label: string;
  note: string;
}

export interface Step {
  id: string;
  /** Mono label, top left of the panel. */
  kicker: string;
  title: string;
  blurb: string;
  fields: Field[];
  /** Earned once every required field in the step is answered. */
  badge: Badge;
}

const identity = (blurb: string): Step => ({
  id: 'identity',
  kicker: 'Call sheet',
  title: 'Who is on the call sheet?',
  blurb,
  fields: [
    { key: 'full_name', label: 'Full name', kind: 'text', placeholder: 'The name that goes in the credits', xp: 40 },
    { key: 'country', label: 'Country', kind: 'text', placeholder: 'India', xp: 30 },
    { key: 'city', label: 'City', kind: 'text', placeholder: 'Mumbai', xp: 20, optional: true },
    { key: 'phone', label: 'Mobile', kind: 'tel', hint: 'Only used when a project needs you quickly.', placeholder: '+91', xp: 30, optional: true },
    {
      key: 'bio',
      label: 'Short bio',
      kind: 'textarea',
      hint: 'Two lines. What you make, and what you are chasing.',
      placeholder: 'I make…',
      xp: 60
    }
  ],
  badge: { id: 'identity', label: 'Identity on file', note: 'The crew knows who you are.' }
});

const PROFESSIONAL: Step[] = [
  identity('Your name and face travel with every frame you publish here.'),
  {
    id: 'craft',
    kicker: 'Craft',
    title: 'What do you do on set?',
    blurb: 'This is the line companies scan first. Pick the one you would be hired for, then the rest you cover.',
    fields: [
      { key: 'primary_role', label: 'Primary role', kind: 'single', options: SPECIALIZATIONS, xp: 80 },
      { key: 'experience', label: 'Years in the craft', kind: 'single', options: EXPERIENCE_BANDS, xp: 50 },
      {
        key: 'specializations',
        label: 'Also strong in',
        kind: 'multi',
        options: SPECIALIZATIONS,
        max: 5,
        hint: 'Up to five. Breadth helps discovery; a wall of tags does not.',
        xp: 70
      }
    ],
    badge: { id: 'craft', label: 'Craft declared', note: 'You are searchable by role.' }
  },
  {
    id: 'toolkit',
    kicker: 'Toolkit',
    title: 'What is in your kit?',
    blurb: 'Briefs are matched on tools as often as on craft. Every one you add widens the net.',
    fields: [
      { key: 'ai_tools', label: 'AI tools', kind: 'multi', options: AI_TOOLS, max: 10, xp: 70 },
      { key: 'production_skills', label: 'Production skills', kind: 'multi', options: PRODUCTION_SKILLS, max: 8, xp: 60 }
    ],
    badge: { id: 'toolkit', label: 'Toolkit logged', note: 'Tool-matched briefs can reach you.' }
  },
  {
    id: 'market',
    kicker: 'Availability',
    title: 'What work do you want?',
    blurb: 'Say no here so you do not have to say it in your inbox.',
    fields: [
      { key: 'industries', label: 'Industries', kind: 'multi', options: INDUSTRIES, max: 6, xp: 40 },
      { key: 'languages', label: 'Languages', kind: 'multi', options: LANGUAGES, max: 6, xp: 30 },
      { key: 'availability', label: 'Availability', kind: 'single', options: AVAILABILITY, xp: 50 },
      { key: 'project_types', label: 'Preferred project types', kind: 'multi', options: PROJECT_TYPES, max: 6, xp: 40 }
    ],
    badge: { id: 'market', label: 'Open for briefs', note: 'You appear in company searches.' }
  },
  {
    id: 'reel',
    kicker: 'The reel',
    title: 'Show one thing.',
    blurb:
      'A YouTube or Vimeo link is enough — we build the embed. One reel outranks every tag on this page, so it is the single highest-value thing you can add.',
    fields: [
      {
        key: 'showreel_url',
        label: 'Showreel link',
        kind: 'url',
        hint: 'YouTube or Vimeo.',
        placeholder: 'https://youtube.com/watch?v=…',
        xp: 120
      },
      { key: 'portfolio_url', label: 'Portfolio or site', kind: 'url', placeholder: 'https://', xp: 40, optional: true }
    ],
    badge: { id: 'reel', label: 'Reel linked', note: 'Your work plays on your public profile.' }
  }
];

const COMPANY: Step[] = [
  {
    id: 'company',
    kicker: 'The register',
    title: 'Who is commissioning?',
    blurb: 'Artists check this before they answer a brief. A filled-out company gets replies.',
    fields: [
      { key: 'company_name', label: 'Company name', kind: 'text', placeholder: 'Legal or trading name', xp: 60 },
      { key: 'website', label: 'Website', kind: 'url', placeholder: 'https://', xp: 40 },
      { key: 'industry', label: 'Industry', kind: 'single', options: INDUSTRIES, xp: 40 },
      { key: 'company_size', label: 'Company size', kind: 'single', options: COMPANY_SIZES, xp: 30 },
      { key: 'location', label: 'Location', kind: 'text', placeholder: 'City, country', xp: 30 }
    ],
    badge: { id: 'company', label: 'On the register', note: 'Your company is a real entity here.' }
  },
  {
    id: 'contact',
    kicker: 'Front office',
    title: 'Who do artists talk to?',
    blurb: 'One named human. Briefs from a nameless account get half the interest.',
    fields: [
      { key: 'contact_person', label: 'Contact person', kind: 'text', xp: 40 },
      { key: 'designation', label: 'Designation', kind: 'text', placeholder: 'Head of Content', xp: 30 },
      {
        key: 'about',
        label: 'About the company',
        kind: 'textarea',
        hint: 'What you make, and what you are trying to make next.',
        xp: 60
      }
    ],
    badge: { id: 'contact', label: 'Front office open', note: 'Artists know who they are writing to.' }
  },
  {
    id: 'needs',
    kicker: 'The brief',
    title: 'What are you looking for?',
    blurb: 'This seeds your first requirement. You can post it properly the moment you are through.',
    fields: [
      { key: 'needs', label: 'You need', kind: 'multi', options: COMPANY_NEEDS, max: 5, xp: 80 },
      { key: 'budget', label: 'Typical budget', kind: 'single', options: BUDGET_BANDS, xp: 50 },
      { key: 'project_types', label: 'Project types', kind: 'multi', options: PROJECT_TYPES, max: 6, xp: 40 }
    ],
    badge: { id: 'needs', label: 'Brief ready', note: 'Matching crews can be shortlisted for you.' }
  }
];

const STUDENT: Step[] = [
  identity('Coursework here becomes portfolio work later, under your name.'),
  {
    id: 'study',
    kicker: 'Enrolment',
    title: 'Where are you studying?',
    blurb: 'Institutions get their own cohort boards, and student work gets its own shelf.',
    fields: [
      { key: 'institution', label: 'Institution', kind: 'text', placeholder: 'College or university', xp: 50 },
      { key: 'field_of_study', label: 'Field of study', kind: 'text', placeholder: 'Film, design, CS…', xp: 40 },
      { key: 'study_year', label: 'Year', kind: 'single', options: STUDY_YEARS, xp: 30 }
    ],
    badge: { id: 'study', label: 'Enrolled', note: 'You are on the Academy roll.' }
  },
  {
    id: 'goals',
    kicker: 'Syllabus',
    title: 'What should we teach you first?',
    blurb: 'Pick the tracks you want and the Academy orders itself around them.',
    fields: [
      { key: 'tracks', label: 'Tracks', kind: 'multi', options: ACADEMY_TRACKS, max: 5, xp: 80 },
      { key: 'skill_level', label: 'Where you are now', kind: 'single', options: SKILL_LEVELS, xp: 40 },
      { key: 'time_commitment', label: 'Time you can give', kind: 'single', options: TIME_COMMITMENT, xp: 30 }
    ],
    badge: { id: 'goals', label: 'Syllabus set', note: 'Your first assignment is waiting.' }
  }
];

const BEGINNER: Step[] = [
  identity('No experience assumed. This is only so the studio opens on the right thing.'),
  {
    id: 'interests',
    kicker: 'Direction',
    title: 'What do you want to make?',
    blurb: 'Answer honestly, including "just exploring". It changes what the studio shows you first.',
    fields: [
      { key: 'make_interests', label: 'You want to make', kind: 'multi', options: MAKE_INTERESTS, max: 4, xp: 80 },
      { key: 'skill_level', label: 'Where you are now', kind: 'single', options: SKILL_LEVELS, xp: 40 }
    ],
    badge: { id: 'interests', label: 'Direction chosen', note: 'The studio opens on your thing.' }
  },
  {
    id: 'commitment',
    kicker: 'First call',
    title: 'How much time have you got?',
    blurb: 'We size your first project to fit it. Small and finished beats big and abandoned.',
    fields: [
      { key: 'time_commitment', label: 'Time you can give', kind: 'single', options: TIME_COMMITMENT, xp: 40 },
      { key: 'tracks', label: 'Start with', kind: 'multi', options: ACADEMY_TRACKS, max: 3, xp: 60 }
    ],
    badge: { id: 'commitment', label: 'First call sheet', note: 'Your first project is scoped.' }
  }
];

export const STEPS: Record<UserType, Step[]> = {
  professional: PROFESSIONAL,
  company: COMPANY,
  student: STUDENT,
  beginner: BEGINNER
};

export function stepsFor(userType: UserType): Step[] {
  return STEPS[userType];
}

export function fieldsFor(userType: UserType): Field[] {
  return STEPS[userType].flatMap(step => step.fields);
}
